import moment from 'moment';
import { toastr } from 'react-redux-toastr';

import { ApiService } from 'services';
import { handleException } from 'actions/exceptions';
import { downloadFile } from 'actions/common';

export const EXPORT_TO_EXCEL_REQUEST = 'EXPORT_TO_EXCEL_REQUEST';
export const EXPORT_TO_EXCEL_SUCCESS = 'EXPORT_TO_EXCEL_SUCCESS';
export const EXPORT_TO_EXCEL_FAILURE = 'EXPORT_TO_EXCEL_FAILURE';

function requestExportToExcel() {
  return {
    type: EXPORT_TO_EXCEL_REQUEST,
    isExportingToExcel: true
  };
}

function receiveExportToExcel(excelData) {
  return {
    type: EXPORT_TO_EXCEL_SUCCESS,
    isExportingToExcel: false,
    excelData,
    exportToExcelError: null
  };
}

function rejectExportToExcel(exportToExcelError) {
  return {
    type: EXPORT_TO_EXCEL_FAILURE,
    isExportingToExcel: false,
    exportToExcelError
  };
}

export function exportToExcel(criteria) {
  return (dispatch) => {
    dispatch(requestExportToExcel());

    const promise = () => ApiService
      .getExcelReporting(criteria, { responseType: 'arraybuffer' })
      .then((response) => {
        dispatch(receiveExportToExcel(response.data));
        return dispatch(
          downloadFile(
            response.data,
            `${ moment().format('DD_MMM_YYYY') }_report.xlsx`,
            'application/xls'
          )
        );
      }).catch((error) => {
        if (!error.response && error.retries && error.retries > 1) {
          ApiService.addQueuedPromise(promise, error);
          return;
        }

        if (error.response && error.response.status === 422) {
          dispatch(rejectExportToExcel(null));
          toastr.error('Report limit exceeded', 'Your filtered search matches more records than can be included in a single report. Please narrow your filters and try again.');
          return;
        }

        if (error.response || (error.retries && error.retries > 1)) {
          dispatch(handleException(error, rejectExportToExcel, true));
        }
      });
    return promise();
  };
}

export function exportWhsManagementToExcel(gridCriteria) {
  return (dispatch) => {
    dispatch(requestExportToExcel());

    const promise = () => ApiService
      .getWhsManagementExport(gridCriteria, { responseType: 'arraybuffer' })
      .then((response) => {
        dispatch(receiveExportToExcel(response.data));
        let fileName = response.headers['content-disposition'].split('filename=')[1].split(';')[0];
        const firstChar = fileName.substring(0, 1);
        if (firstChar === '"') {
          fileName = fileName.slice(1, -1);
        }
        return dispatch(
          downloadFile(
            response.data,
            fileName,
            'application/xls'
          )
        );
      }).catch((error) => {
        if (!error.response && error.retries && error.retries > 1) {
          ApiService.addQueuedPromise(promise, error);
        }
        if (error.response || (error.retries && error.retries > 1)) {
          dispatch(handleException(error, rejectExportToExcel, true));
        }
      });
    return promise();
  };
}

export function getCustomerExportFile(requestObject) {
  return (dispatch) => {
    dispatch(requestExportToExcel());
    const promise = () => ApiService
      .getCustomerExport(
        requestObject.pageNumber,
        requestObject.pageSize,
        requestObject.totalLimit,
        requestObject.sortByField,
        requestObject.sortDirection,
        requestObject.filterList,
        requestObject.searchQuery,
        requestObject.legalEntity,
        requestObject.legalEntitySelected,
        requestObject.accountNumberSelected,
        requestObject.accountNumber,
        requestObject.customer,
        requestObject.customerSelected,
        { responseType: 'arraybuffer' }
      )
      .then((response) => {
        dispatch(receiveExportToExcel(response.data));
        return dispatch(
          downloadFile(
            response.data,
            `customer_dashboard_export_${ moment().format('DD_MMM_YYYY') }.xlsx`,
            'application/xls'
          )
        );
      }).catch((error) => {
        if (!error.response && error.retries && error.retries > 1) {
          ApiService.addQueuedPromise(promise, error);
        }
        if (error.response || (error.retries && error.retries > 1)) {
          dispatch(handleException(error, rejectExportToExcel, true));
        }
      });
    return promise();
  };
}

export function getAllFormsReportFile(requestObject) {
  return (dispatch) => {
    dispatch(requestExportToExcel());
    const promise = () => ApiService
      .getAllFormsReport(
        requestObject.pageNumber,
        requestObject.pageSize,
        requestObject.totalLimit,
        requestObject.sortByField,
        requestObject.sortDirection,
        requestObject.filterList,
        requestObject.searchQuery,
        requestObject.legalEntity,
        requestObject.legalEntitySelected,
        requestObject.accountNumber,
        requestObject.accountNumberSelected,
        requestObject.customer,
        requestObject.customerSelected,
        requestObject.formFamilies,
        { responseType: 'arraybuffer' }
      )
      .then((response) => {
        dispatch(receiveExportToExcel(response.data));

        let fileName = `ALLFORMS-${ moment().format('YYYYMMDD-HHmm') }.xlsx`;
        const contentDisposition = response.headers['content-disposition'];
        if (contentDisposition) {
          const match = contentDisposition.match(/filename\*?=(?:UTF-8''|)?"?([^";]+)"?/i);
          const [, rawFileName] = match || [];
          if (rawFileName) {
            try {
              fileName = decodeURIComponent(rawFileName);
            } catch (e) {
              fileName = rawFileName;
            }
          }
        }

        return dispatch(
          downloadFile(
            response.data,
            fileName,
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          )
        );
      }).catch((error) => {
        if (!error.response && error.retries && error.retries > 1) {
          ApiService.addQueuedPromise(promise, error);
          return;
        }

        if (error.response && error.response.status === 422) {
          dispatch(rejectExportToExcel(null));
          toastr.error('Report limit exceeded', 'Your filtered search matches more records than can be included in a single report. Please narrow your filters and try again.');
          return;
        }

        if (error.response || (error.retries && error.retries > 1)) {
          dispatch(handleException(error, rejectExportToExcel, true));
        }
      });
    return promise();
  };
}

export function getAllocationStatementFile(formId) {
  return (dispatch) => {
    dispatch(requestExportToExcel());

    const promise = () => ApiService
      .getAllocationStatementFile(formId, { responseType: 'arraybuffer' })
      .then((response) => {
        dispatch(receiveExportToExcel(response.data));
        let fileName = response.headers['content-disposition'].split('filename=')[1].split(';')[0];
        const firstChar = fileName.substring(0, 1);
        if (firstChar === '"') {
          fileName = fileName.slice(1, -1);
        }
        return dispatch(
          downloadFile(
            response.data,
            fileName,
            'application/octet-stream'
          )
        );
      }).catch((error) => {
        if (!error.response && error.retries && error.retries > 1) {
          ApiService.addQueuedPromise(promise, error);
        }
        if (error.response || (error.retries && error.retries > 1)) {
          dispatch(handleException(error, rejectExportToExcel, true));
        }
      });
    return promise();
  };
}

export function getAllocationStatementFileByAccount(accountId) {
  return (dispatch) => {
    dispatch(requestExportToExcel());

    const promise = () => ApiService
      .getAllocationStatementFileByAccount(accountId, { responseType: 'arraybuffer' })
      .then((response) => {
        dispatch(receiveExportToExcel(response.data));
        let fileName = response.headers['content-disposition'].split('filename=')[1].split(';')[0];
        const firstChar = fileName.substring(0, 1);
        if (firstChar === '"') {
          fileName = fileName.slice(1, -1);
        }
        return dispatch(
          downloadFile(
            response.data,
            fileName,
            'application/octet-stream'
          )
        );
      }).catch((error) => {
        if (!error.response && error.retries && error.retries > 1) {
          ApiService.addQueuedPromise(promise, error);
        }
        if (error.response || (error.retries && error.retries > 1)) {
          dispatch(handleException(error, rejectExportToExcel, true));
        }
      });
    return promise();
  };
}

export function exportWhdStatementXls(accountId, versionNumber) {
  return (dispatch) => {
    dispatch(requestExportToExcel());

    const promise = () => ApiService
      .exportWhdStatementXls(accountId, versionNumber, { responseType: 'arraybuffer' })
      .then((response) => {
        dispatch(receiveExportToExcel(response.data));

        let fileName;
        if (versionNumber) {
          fileName = `withholding-statement-${ accountId }-v${ versionNumber }.xlsx`;
        } else {
          fileName = `withholding-statement-${ accountId }.xlsx`;
        }

        if (response.headers['content-disposition']) {
          const [, dispositionFileName] = response.headers['content-disposition'].split('filename=');
          const [parsedFileName] = dispositionFileName.split(';');
          fileName = parsedFileName;
          const firstChar = fileName.substring(0, 1);
          if (firstChar === '"') {
            fileName = fileName.slice(1, -1);
          }
        }

        return dispatch(
          downloadFile(
            response.data,
            fileName,
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          )
        );
      }).catch((error) => {
        if (!error.response && error.retries && error.retries > 1) {
          ApiService.addQueuedPromise(promise, error);
        }
        if (error.response || (error.retries && error.retries > 1)) {
          dispatch(handleException(error, rejectExportToExcel, true));
        }
      });
    return promise();
  };
}
